import React, { useEffect, useMemo } from 'react';
import {
  Button,
  Col,
  FlexboxGrid,
  Footer,
  Grid,
  Message,
  Row,
  Text,
} from 'rsuite';
import PortalHeader from '../components/layout/PortalHeader';
import LoginWidget from '../components/auth/LoginWidget';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectConfigLoadFailed,
  selectIsExpressConfigLoaded,
  selectConfigSettings,
  selectConfigExpressSettingsName,
} from '../store/slices/config/configSelectors';
import { getTextFromRentalType } from '../common/textUtils';
import usePlaceNavigate from '../hooks/usePlaceNavigate';
import pluralize from 'pluralize';
import { constructMarkup } from '../common/utils';
import { TAX_PAYMENT_PERIOD_TYPES_ENUM } from '../common/enums';
import { setCreateAccountSteps } from '../store/slices/flow/flowThunks';
import { clearPersistedState } from '../common/tokenPersistence';
import { selectCurrentAccessToken } from '../store/slices/auth/authSelectors';
import { jwtDecode } from 'jwt-decode';

const Landing = () => {
  const dispatch = useDispatch();
  const placeNavigate = usePlaceNavigate();
  const isConfigLoaded = useSelector(selectIsExpressConfigLoaded);
  const config = useSelector(selectConfigSettings);
  const placeDisplayName = useSelector(selectConfigExpressSettingsName);
  const { express, totPaymentPeriod, rentalType } = config;

  const selectedConfigLoadFailed = useSelector(selectConfigLoadFailed);
  const token = useSelector(selectCurrentAccessToken);

  const headerText = useMemo(() => {
    return `Welcome to the ${placeDisplayName} ${express?.verbiage} Registration ${express?.tax?.enable ? 'and Tax' : ''} Portal!`;
  }, [placeDisplayName, express]);

  const totPaymentPeriodText = useMemo(() => {
    return totPaymentPeriod === TAX_PAYMENT_PERIOD_TYPES_ENUM.NY_FISCAL
      ? 'NY fiscal quarter'
      : totPaymentPeriod;
  }, [totPaymentPeriod]);

  const paragraphOneText = useMemo(() => {
    const licenseVerbiage = express?.registration?.verbiage;
    return (
      `All ${getTextFromRentalType(rentalType)} within ${placeDisplayName} are required to hold a valid ${licenseVerbiage}` +
      `${express?.tax?.enable ? ` and remit taxes ${totPaymentPeriodText} for nights that the property is occupied by guests` : ''}.`
    );
  }, [placeDisplayName, totPaymentPeriodText, express, rentalType]);

  const paragraphTwoText = useMemo(() => {
    const licenseVerbiageShort = express?.registration?.type_verbiage;
    if (!licenseVerbiageShort) {
      return;
    }
    return (
      `This portal allows ${licenseVerbiageShort} Holders to manage their ${pluralize(licenseVerbiageShort)?.toLowerCase()}` +
      `${express?.tax?.enable ? `, report and remit their tax, and view their history of past remittances` : ''}.`
    );
  }, [express]);

  useEffect(() => {
    if (selectedConfigLoadFailed) placeNavigate('/error');
  }, [selectedConfigLoadFailed]);

  useEffect(() => {
    clearPersistedState();
    dispatch({ type: 'resetRegSystem' });
  }, []);

  useEffect(() => {
    // If token exists, check if it's valid
    if (token) {
      try {
        const decoded = jwtDecode(token);
        const expirationTime = decoded.exp;
        const currentTime = Math.floor(Date.now() / 1000);
        // this avoids if the token is valid and the user is logged in go to the login page, redirects to dashboard
        if (expirationTime && expirationTime > currentTime) {
          placeNavigate('/dashboard', { replace: true });
        } else {
          dispatch({ type: 'resetRegSystem' });
        }
      } catch (e) {
        // If token is invalid/can't be decoded
        console.error(e);
        dispatch({ type: 'resetRegSystem' });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onCreateAccountClicked = () => {
    dispatch(setCreateAccountSteps(onCreateAccountStepsMapped));
  };

  const onCreateAccountStepsMapped = () => {
    placeNavigate('/email-check');
  };

  return (
    <FlexboxGrid
      className="App"
      justify="space-between"
    >
      <FlexboxGrid.Item colspan={24}>
        <PortalHeader
          title={placeDisplayName}
          showAccountDropdown={false}
        />
      </FlexboxGrid.Item>

      {isConfigLoaded && (
        <FlexboxGrid.Item
          as="main"
          role="main"
          className="ph-24 pb-24 mt-48 sm-mt-0 sm-page-padding landing-page-content-container"
          colspan={24}
        >
          <FlexboxGrid
            as={Grid}
            justify="space-between"
            className="mb-24"
            fluid
          >
            <Row className="sm-p-0">
              <Col
                xs={24}
                lg={16}
                className="mb-24"
              >
                <Text className="header-text-h4 mb-12">{headerText}</Text>
                <div className="content-section ph-24 pv-16">
                  <Text className="paragraph">{paragraphOneText}</Text>
                  <Text className="paragraph">{paragraphTwoText}</Text>
                  <Text
                    className={`paragraph ${express?.landing_addendum ? 'mb-24' : ''}`}
                  >
                    {`To apply for a ${express?.registration?.verbiage}, start by
                      creating an account using the button below. If you
                      already have an account, please login using the
                      panel on the right-hand side of the page. You can
                      use the same account to manage ${pluralize(express?.registration?.type_verbiage)?.toLowerCase()} for multiple
                      properties.`}
                  </Text>
                  {express?.landing_addendum && (
                    <Message>
                      <Text className="text-large text-bold">
                        {express?.landing_addendum?.title}
                      </Text>
                      <Text className="mb-8">
                        {constructMarkup(
                          express?.landing_addendum?.text,
                          'light-contrast-link-text'
                        )}
                      </Text>
                    </Message>
                  )}
                </div>
                <Button
                  className="mt-16 btn-primary sm-100-percent"
                  onClick={onCreateAccountClicked}
                >
                  Create Account
                </Button>
              </Col>
              <Col
                xs={24}
                lg={8}
              >
                <Text className="header-text-h4 mb-12">Account Login</Text>
                <div className="content-section ph-24 pt-16">
                  <LoginWidget
                    errorMessage="Email address is required"
                    buttonText="Login"
                  />
                </div>
              </Col>
            </Row>
          </FlexboxGrid>
        </FlexboxGrid.Item>
      )}

      <FlexboxGrid.Item
        colspan={24}
        className="mb-16"
      >
        <Footer>
          <FlexboxGrid
            as={Grid}
            align="bottom"
            justify="center"
          >
            <Row>
              <Col xs={24}>
                <a
                  href="https://www.deckard.com/"
                  target="_blank"
                  rel="noreferrer"
                  className="no-underline "
                >
                  <Text className="mb-4 text-small">Powered by:</Text>
                  <img
                    src="/assets/deckard-logo-horizontal-gray-ai.png"
                    alt="Deckard Technologies Logo"
                  />
                </a>
              </Col>
            </Row>
          </FlexboxGrid>
        </Footer>
      </FlexboxGrid.Item>
    </FlexboxGrid>
  );
};

export default Landing;
